// --- Dependencies -------------------------------------------------------------------------------
import { useLayoutEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportTicket, TySupportTicketStatus } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    ticket: TySupportTicket;
    onDelete?: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function SupportTicketCard({ ticket, onDelete }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const messageRef = useRef<HTMLParagraphElement>(null);

    /** The toggle only makes sense when the message actually overflows its clamped height. */
    useLayoutEffect(() => {
        const element = messageRef.current;
        if (!element) return;
        setIsClamped(element.scrollHeight > element.clientHeight + 1);
    }, [ticket.message]);

    const statusLabels: Record<TySupportTicketStatus, string> = {
        pending: t.support.statusPending,
        contacted: t.support.statusContacted,
        solved: t.support.statusSolved,
        deleted: t.support.statusPending,
    };

    const date = new Date(ticket.createdAt);
    const dateLabel = isNaN(date.getTime())
        ? "--"
        : date.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { day: "numeric", month: "long", year: "numeric" });

    return (
        <div className="support-ticket-card">
            {/* --- Header: title + status ----------------------------------------------------- */}
            <div className="support-ticket-card-header">
                <p className="support-ticket-card-title">{ticket.title}</p>
                <span className={`support-ticket-card-status support-ticket-card-status--${ticket.status}`}>{statusLabels[ticket.status]}</span>
            </div>

            {/* --- Message -------------------------------------------------------------------- */}
            <p ref={messageRef} className={`support-ticket-card-message${isExpanded ? " support-ticket-card-message--expanded" : ""}`}>
                {ticket.message}
            </p>
            {(isClamped || isExpanded) && (
                <button type="button" className="support-ticket-card-toggle" onClick={() => setIsExpanded((prev) => !prev)}>
                    {isExpanded ? t.support.readLess : t.support.readMore}
                </button>
            )}

            {/* --- Footer: date + delete ------------------------------------------------------ */}
            <div className="support-ticket-card-footer">
                <span className="support-ticket-card-date">{dateLabel}</span>
                {onDelete && (
                    <button type="button" className="support-ticket-card-delete" onClick={onDelete}>
                        <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                        <span>{t.support.deleteAction}</span>
                    </button>
                )}
            </div>
        </div>
    );
}
